import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { ApiClient } from '@/services/api.service';
import { SuccessNotificationIcon, ErrorNotificationIcon } from '@/assets/icons';
import { useToast } from './useToast';
import { MessageDto } from '@/types/message';

export const EDIT_MODE: ChatMode = 'edit'
export const REPLY_MODE: ChatMode = 'reply'

export const EDITING_FINISHED = 'editing_finished'
export const REPLY_FINISHED = 'reply_finished'

export type Suggestion = {
    id: number
    phrase: string
}

type ChatMode =
    null |
    'edit' |
    'reply'

type ContextMsg =
    {
        id: number | string,
        content: string,
        is_mine?: boolean,
        status?: boolean,
        created_at?: Date,
        pos?: {
            top: number | null
            bottom: number | null
        },
        reply_message_id: number | null,
        has_reply: boolean,
    } | null

interface ChatStore {
    msg: string;
    messages: MessageDto[];
    setMsg: (val: string) => void;
    setMessages: (val: (msgs: MessageDto[]) => MessageDto[]) => void;
    chatMode: ChatMode;
    prevChatMode: ChatMode;
    setChatMode: (val: ChatMode) => void;
    contextMsg: ContextMsg;
    setContextMsg: (msg: ContextMsg) => void;
    clearContextMsg: () => void;
    status: string | null;
    setStatus: (msg: string | null) => void;
    menu: string | number | null;
    setMenu: (id: string | number) => void;
    clearMenu: () => void;
    generateSuggestion: (
        params: RecipientParams,
        onSuccess: (msg: Suggestion) => void,
        onError: () => void,
    ) => Promise<void>;
    init: () => void;
}

export type RecipientParams = {
    recipient_id: string | number | undefined
}

export const useChat = create<ChatStore>()(
    devtools(
        immer((set, get) => ({
            msg: '',
            messages: [],
            setMsg: (val: string) => {
                set({
                    msg: val
                })
            },
            setMessages: (val: (msgs: MessageDto[]) => MessageDto[]) => {
                set({
                    messages: val(get().messages)
                })
            },
            chatMode: null,
            prevChatMode: null,
            setChatMode: async (val: ChatMode) => {
                const chatMode = get().chatMode
                set({
                    prevChatMode: chatMode
                })
                set({
                    chatMode: val
                })
            },
            contextMsg: null,
            status: null,
            setContextMsg: (msg: ContextMsg) => {
                set({
                    contextMsg: msg
                })
            },
            menu: null,
            clearContextMsg: () => {
                set({
                    contextMsg: null
                })
            },
            setStatus: async (val: string | null) => {
                set({
                    status: val
                })
            },
            setMenu: async (val: string | number) => {
                set({
                    menu: val
                })
            },
            clearMenu: async () => {
                set({
                    menu: null
                })
            },
            generateSuggestion: async (params, onSuccess, onError) => {
                try {
                    const { data } = await ApiClient.get<Suggestion>(`/suggestion/`, { params })
                    set({ msg: data.phrase })
                    onSuccess(data)
                } catch {
                    onError()
                }
            },
            init: () => {
                console.log('init chat')
                set({
                    msg: '',
                    messages: [],
                    chatMode: null,
                    prevChatMode: null,
                    contextMsg: null,
                    status: null,
                    menu: null,
                })
            }
        })),
    ),
);
